import React, { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Tabs from "src/components/ui/Tabs";
import TenantUserForm from "src/components/forms/TenantUserForm";

import { useAppSelector } from "src/redux/hooks";

const CreateEditUserPage: React.FC = () => {
  const basePath = "/app/team-management";
  const navigate = useNavigate();
  const { tenantUserId } = useParams();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);

  const permissions = useMemo(
    () => user?.roleDetails?.permissions || user?.permissions || {},
    [user?.roleDetails?.permissions, user?.permissions]
  );
  const permissionEntries = Object.keys(permissions || {});
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canAccessTeamManagement =
    isTenantAdmin ||
    permissionEntries.length === 0 ||
    (permissions?.users || []).includes("view");
  const canManageRoles = isTenantAdmin;

  const tabs = useMemo(() => {
    const items = [{ label: "Users", value: "users" }];
    if (canManageRoles) {
      items.push({ label: "Roles", value: "roles" });
    }
    return items;
  }, [canManageRoles]);

  if (!canAccessTeamManagement) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <DashboardLayout
      meta={{
        title: `${tenantUserId ? "Edit" : "Create"} User | Invyntra`,
        description: "Manage tenant users for your workspace.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={tenantUserId ? "Edit User" : "Create User"}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Team management", path: `${basePath}/user-list` },
            {
              label: tenantUserId ? "Edit User" : "Create User",
              path: tenantUserId
                ? `${basePath}/user/${tenantUserId}/edit`
                : `${basePath}/user/create`,
            },
          ]}
        />

        <Card>
          <Tabs
            tabs={tabs}
            activeTab="users"
            onChange={(value) =>
              navigate(
                `${basePath}/${value === "roles" ? "role-list" : "user-list"}`
              )
            }
            className="flex gap-2 border-b border-gray-200"
          />

          <div className="pt-4">
            <TenantUserForm
              tenantUserId={tenantUserId}
              onCancel={() => navigate(`${basePath}/user-list`)}
              onSuccess={() => navigate(`${basePath}/user-list`)}
            />
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CreateEditUserPage;
